<template>
  <section class="billing-page">
    <div class="billing-container">
      <div class="header">
        <div>
          <p class="eyebrow">Billing</p>
          <h1>Plan & Call Usage</h1>
          <p class="subtitle">
            Manage your AI receptionist plan, monitor your monthly call usage,
            and activate the next step when your free calls are used.
          </p>
        </div>

        <div class="current-plan-badge">
          {{ currentPlan.name }}
        </div>
      </div>

      <div class="usage-card">
        <div class="usage-top">
          <div>
            <p class="label">Current plan</p>
            <h2>{{ currentPlan.name }}</h2>
          </div>

          <div class="usage-number">
            {{ usagePercentage }}%
          </div>
        </div>

        <div class="usage-details">
          <p>
            <strong>{{ currentPlan.used.toLocaleString() }}</strong>
            of
            <strong>{{ currentPlan.limit.toLocaleString() }}</strong>
            calls used
          </p>

          <p class="muted">
            {{ remainingCalls.toLocaleString() }} calls remaining in this billing period.
          </p>
        </div>

        <div class="progress-wrapper">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: usagePercentage + '%' }"
            ></div>
          </div>
        </div>

        <div v-if="currentPlan.isTrial" class="trial-box">
          After your first 10 free calls, continue with 100 calls for $250.
          Activation is handled by our team while Stripe checkout is being finalized.
        </div>

        <div v-else-if="usagePercentage >= 90" class="warning-box">
          You are close to your monthly call limit. Consider upgrading to avoid
          missed opportunities.
        </div>
      </div>

      <div class="plans-header">
        <div>
          <h2>Choose your plan</h2>
          <p>
            Start with the founding customer plan, then upgrade when you need more call volume,
            more agents, or advanced workflows.
          </p>
        </div>
      </div>

      <div class="plans-grid">
        <div
          v-for="plan in availablePlans"
          :key="plan.name"
          class="plan-card"
          :class="{
            featured: plan.featured,
            active: plan.name === currentPlan.name
          }"
        >
          <div class="plan-top">
            <div>
              <h3>{{ plan.name }}</h3>
              <p class="plan-description">{{ plan.description }}</p>
            </div>

            <span v-if="plan.name === currentPlan.name" class="active-pill">
              Current
            </span>

            <span v-else-if="plan.featured" class="featured-pill">
              Recommended
            </span>
          </div>

          <div class="price">
            <span>{{ plan.price }}</span>
            <small>{{ plan.billing }}</small>
          </div>

          <ul class="features-list">
            <li v-for="feature in plan.features" :key="feature">
              <span class="check">✓</span>
              {{ feature }}
            </li>
          </ul>

          <button
            type="button"
            :disabled="plan.name === currentPlan.name"
            @click="selectPlan(plan)"
          >
            {{
              plan.name === currentPlan.name
                ? 'Current plan'
                : plan.cta
            }}
          </button>
        </div>
      </div>

      <div class="enterprise-box">
        <div>
          <h2>Need more call volume or custom workflows?</h2>
          <p>
            For high-call-volume teams, multi-location businesses, or custom
            integrations, contact us for a tailored plan.
          </p>
        </div>

        <button type="button" class="secondary-button" @click="contactSales">
          Contact sales
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import Cookies from 'js-cookie'

export default {
  name: 'PlanRequests',

  data() {
    return {
      currentPlan: {
        name: 'Trial',
        used: 0,
        limit: 10,
        unit: 'calls',
        isTrial: true
      },

      availablePlans: [
        {
          name: 'Founding Customer',
          price: '$250',
          billing: '',
          description: 'For businesses ready to test an AI receptionist on real calls.',
          features: [
            '100 calls included',
            '1 AI receptionist',
            'Dedicated phone number included',
            'Missed call handling',
            'Lead capture',
            'Basic appointment booking'
          ],
          cta: 'Request activation',
          featured: true
        },
        {
          name: 'Growth',
          price: '$500',
          billing: '/ month',
          description: 'For businesses that want consistent call coverage.',
          features: [
            '150 calls included per month',
            '1 AI receptionist',
            'Dedicated phone number included',
            'Appointment booking workflows',
            'Lead qualification',
            'SMS confirmation'
          ],
          cta: 'Upgrade to Growth',
          featured: false
        },
        {
          name: 'Business',
          price: '$1,000',
          billing: '/ month',
          description: 'For teams with higher call volume and stronger automation needs.',
          features: [
            '400 calls included per month',
            'Up to 3 AI receptionists',
            'Dedicated phone number included',
            'Advanced workflows',
            'Booking and follow-up automation',
            'CRM/webhook integration'
          ],
          cta: 'Upgrade to Business',
          featured: false
        },
        {
          name: 'Scale',
          price: 'Custom',
          billing: '',
          description:
            'For high-volume operations, multi-location businesses, or custom deployments.',
          features: [
            'Custom call volume',
            'Multiple AI receptionists',
            'Dedicated onboarding',
            'Custom integrations',
            'SLA available',
            'Volume pricing'
          ],
          cta: 'Contact sales',
          featured: false
        }
      ]
    }
  },

  computed: {
    usagePercentage() {
      if (!this.currentPlan.limit) return 0

      const percentage = Math.round(
        (this.currentPlan.used / this.currentPlan.limit) * 100
      )

      return Math.min(percentage, 100)
    },

    remainingCalls() {
      return Math.max(this.currentPlan.limit - this.currentPlan.used, 0)
    }
  },

  mounted() {
    const token = Cookies.get('token')

    if (!token) {
      this.$router.push('/login')
    }
  },

  methods: {
    selectPlan(plan) {
      if (plan.name === this.currentPlan.name) return

      if (plan.name === 'Scale') {
        this.contactSales()
        return
      }

      this.requestActivation(plan)
    },

    contactSales() {
      window.location.href =
        'mailto:contact@nocodeagent.ai?subject=NoCodeAgent Custom Plan'
    },

    requestActivation(plan) {
      const subject = encodeURIComponent(`Activate NoCodeAgent ${plan.name}`)
      const body = encodeURIComponent(
        `I want to activate ${plan.name}.\n\nOffer: 100 calls for $250 after the first 10 free calls.\n\nPlease send next steps.`
      )
      window.location.href = `mailto:contact@nocodeagent.ai?subject=${subject}&body=${body}`
    }
  }
}
</script>

<style scoped>
.billing-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 48px 20px;
  color: #0f172a;
}

.billing-container {
  max-width: 1180px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 28px;
}

.eyebrow {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}

h1 {
  font-size: 2.6rem;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #020617;
  margin-bottom: 10px;
}

.subtitle {
  max-width: 680px;
  color: #64748b;
  line-height: 1.7;
}

.current-plan-badge {
  background: #020617;
  color: white;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 800;
  white-space: nowrap;
}

.usage-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 28px;
  padding: 28px;
  box-shadow: 0 20px 70px rgba(15, 23, 42, 0.06);
  margin-bottom: 34px;
}

.usage-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.label {
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
}

.usage-top h2 {
  font-size: 1.8rem;
  font-weight: 900;
  color: #020617;
}

.usage-number {
  font-size: 2rem;
  font-weight: 900;
  color: #020617;
}

.usage-details {
  margin-top: 20px;
  color: #334155;
  line-height: 1.7;
}

.muted {
  color: #64748b;
  font-size: 0.95rem;
}

.progress-wrapper {
  margin-top: 20px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #0f172a);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.trial-box {
  margin-top: 18px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 0.92rem;
  font-weight: 600;
}

.warning-box {
  margin-top: 18px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 0.92rem;
  font-weight: 600;
}

.plans-header {
  margin-bottom: 18px;
}

.plans-header h2 {
  font-size: 1.6rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 6px;
}

.plans-header p {
  color: #64748b;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.plan-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 26px;
  padding: 22px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  min-height: 410px;
  transition: transform 0.2s ease, border 0.2s ease;
}

.plan-card:hover {
  transform: translateY(-3px);
  border-color: rgba(15, 23, 42, 0.2);
}

.plan-card.featured {
  border: 2px solid #0f172a;
}

.plan-card.active {
  background: #f8fafc;
}

.plan-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  min-height: 92px;
}

.plan-top h3 {
  font-size: 1.25rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.plan-description {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
}

.active-pill,
.featured-pill {
  border-radius: 999px;
  padding: 7px 10px;
  font-size: 0.7rem;
  font-weight: 900;
  white-space: nowrap;
}

.active-pill {
  background: #dcfce7;
  color: #166534;
}

.featured-pill {
  background: #020617;
  color: white;
}

.price {
  margin: 22px 0;
}

.price span {
  font-size: 2rem;
  font-weight: 900;
  color: #020617;
}

.price small {
  color: #64748b;
  font-weight: 700;
  margin-left: 4px;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: grid;
  gap: 12px;
  color: #334155;
  font-size: 0.92rem;
  line-height: 1.45;
}

.features-list li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.check {
  color: #16a34a;
  font-weight: 900;
}

.plan-card button,
.secondary-button {
  margin-top: auto;
  width: 100%;
  border: none;
  border-radius: 16px;
  background: #020617;
  color: white;
  padding: 14px 16px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.plan-card button:hover,
.secondary-button:hover {
  transform: scale(1.02);
  background: #111827;
}

.plan-card button:disabled {
  background: #cbd5e1;
  color: #475569;
  cursor: not-allowed;
  transform: none;
}

.enterprise-box {
  margin-top: 26px;
  background: #020617;
  color: white;
  border-radius: 28px;
  padding: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.enterprise-box h2 {
  font-size: 1.5rem;
  font-weight: 900;
  margin-bottom: 8px;
}

.enterprise-box p {
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.6;
}

.secondary-button {
  width: auto;
  min-width: 180px;
  background: white;
  color: #020617;
}

.secondary-button:hover {
  background: #e2e8f0;
}

@media (max-width: 1024px) {
  .plans-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .enterprise-box {
    flex-direction: column;
    align-items: flex-start;
  }

  .secondary-button {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .billing-page {
    padding: 28px 14px;
  }

  .header,
  .usage-top {
    flex-direction: column;
  }

  h1 {
    font-size: 2rem;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .usage-card,
  .enterprise-box {
    padding: 22px;
    border-radius: 22px;
  }
}
</style>
